import { useState, useCallback, useRef } from "react";
import type { ConfirmDialogProps } from "../components/primitives/feedback/ConfirmDialog";

export interface ConfirmHandlerOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  confirmColor?: ConfirmDialogProps["confirmColor"];
}

export interface UseConfirmHandlerResult {
  /** Call this to trigger the confirmation flow for the given action. */
  handle: (fn: () => Promise<void>) => void;
  /** Spread these onto a <ConfirmDialog />. */
  dialogProps: Pick<
    ConfirmDialogProps,
    | "open"
    | "title"
    | "message"
    | "confirmLabel"
    | "confirmColor"
    | "loading"
    | "error"
    | "onConfirm"
    | "onCancel"
  >;
}

/**
 * Gates an async action behind a confirmation dialog.
 * On failure the dialog stays open and the error is surfaced via dialogProps.error.
 * Caller renders <ConfirmDialog {...dialogProps} /> once; repeated handle() calls
 * swap in a fresh action each time without remounting the dialog.
 */
export function useConfirmHandler({
  title,
  message,
  confirmLabel,
  confirmColor,
}: ConfirmHandlerOptions): UseConfirmHandlerResult {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pendingFn = useRef<(() => Promise<void>) | null>(null);

  const handle = useCallback((fn: () => Promise<void>) => {
    pendingFn.current = fn;
    setError(null);
    setOpen(true);
  }, []);

  const onConfirm = useCallback(async () => {
    const fn = pendingFn.current;
    if (!fn) return;
    setLoading(true);
    setError(null);
    try {
      await fn();
      setOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  }, []);

  const onCancel = useCallback(() => {
    if (loading) return;
    setOpen(false);
    setError(null);
    pendingFn.current = null;
  }, [loading]);

  return {
    handle,
    dialogProps: {
      open,
      title,
      message,
      confirmLabel,
      confirmColor,
      loading,
      error,
      onConfirm,
      onCancel,
    },
  };
}
